import { useLayoutEffect, useMemo, useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import type { ECharts } from "echarts";
import { EChartsRenderer } from "@/shared/charts/components/echarts-renderer";
import {
  createTimeComparisonLineChartAxisOption,
  createTimeComparisonLineChartDataOption,
  createTimeComparisonLineChartShellOption,
} from "@/shared/charts/components/time-comparison-line-chart/option";
import type {
  TimeComparisonLineChartConfig,
  TimeComparisonLineChartPoint,
} from "@/shared/charts/components/time-comparison-line-chart/types";
import { createEChartsTheme } from "@/shared/charts/theme/create-echarts-theme";
import { useStableAxisRange } from "@/shared/charts/utils/use-stable-axis-range";

interface TimeComparisonLineChartProps {
  currentData: TimeComparisonLineChartPoint[];
  comparisonData: TimeComparisonLineChartPoint[];
  now: number | null;
  rangeStart: number;
  rangeEnd: number;
  config: TimeComparisonLineChartConfig;
  height?: number | string;
  testId?: string;
}

/**
 * Renders a live-updating comparison line chart. The static "shell" (legend, tooltip, grid, axis
 * styling) is passed to ECharts declaratively and only rebuilt when the theme/config actually
 * change. Axis bounds are pushed independently whenever the caller's range bounds change, while
 * series data and the "now" marker (a server-supplied reference timestamp) are pushed only when
 * fetched data changes. Both updates bypass the legend/tooltip/grid.
 *
 * This split matters because bounds can update frequently: if every update rebuilt the legend's
 * clickable graphics too, a click landing near an update could be swallowed by ECharts
 * mid-rebuild, making the legend's show/hide toggle feel unresponsive (needing a second click).
 */
export function TimeComparisonLineChart({
  currentData,
  comparisonData,
  now,
  rangeStart,
  rangeEnd,
  config,
  height = 420,
  testId,
}: TimeComparisonLineChartProps) {
  const muiTheme = useTheme();
  const tokens = useMemo(() => createEChartsTheme(muiTheme), [muiTheme]);
  const colors = useMemo(
    () => ({
      current: muiTheme.palette.warning.main,
      comparison: alpha(muiTheme.palette.text.primary, 0.75),
      now: muiTheme.palette.primary.main,
      nowText: muiTheme.palette.primary.contrastText,
    }),
    [muiTheme]
  );
  // Scale to the visible series only. Threshold reference lines (e.g. LoS limits) are
  // annotations, not data, so a far-off threshold shouldn't force the axis to zoom out
  // and compress the actual population trend.
  const yAxisValues = useMemo(
    () => [
      ...currentData.map((point) => point.value),
      ...comparisonData.map((point) => point.value),
    ],
    [currentData, comparisonData]
  );
  const yAxisRange = useStableAxisRange(yAxisValues, { floor: 0, minSpan: 20 });

  const shellOption = useMemo(
    () =>
      createTimeComparisonLineChartShellOption(config, tokens, colors, comparisonData.length > 0),
    [config, tokens, colors, comparisonData.length]
  );
  const axisOption = useMemo(
    () => createTimeComparisonLineChartAxisOption(rangeStart, rangeEnd),
    [rangeStart, rangeEnd]
  );
  const dataOption = useMemo(
    () =>
      createTimeComparisonLineChartDataOption(
        currentData,
        comparisonData,
        now,
        config,
        tokens,
        colors,
        yAxisRange
      ),
    [currentData, comparisonData, now, config, tokens, colors, yAxisRange]
  );

  const [chartInstance, setChartInstance] = useState<ECharts | null>(null);

  useLayoutEffect(() => {
    if (chartInstance == null || chartInstance.isDisposed()) return;
    chartInstance.setOption(axisOption, { notMerge: false, lazyUpdate: true });
  }, [chartInstance, axisOption]);

  useLayoutEffect(() => {
    if (chartInstance == null || chartInstance.isDisposed()) return;
    chartInstance.setOption(dataOption, { notMerge: false, lazyUpdate: true });
  }, [chartInstance, dataOption]);

  return (
    <EChartsRenderer
      option={shellOption}
      ariaLabel={config.ariaDescription}
      height={height}
      testId={testId}
      notMerge={false}
      onChartReady={setChartInstance}
    />
  );
}
